<!-- eslint-disable vue-a11y/click-events-have-key-events -->
<template>
  <button
    :id="buttonWrapperId"
    :class="buttonClassObject"
    :disabled="disabled"
    :title="title"
    :type="type"
    :aria-label="title"
    :style="cssStyle"
    data-samora
    v-on="$listeners"
  >
    <div class="sam-button__content">
      <span
        v-if="label"
        class="sam-button__label"
      >
        {{ label }}
      </span>
      <SamIcon
        v-if="icon"
        :class="['sam-button__icon', { 'icon-only': noLabel }]"
        v-bind="iconProps"
      />
    </div>
    <SamTooltip
      v-if="tooltip"
      :anchor-name="buttonWrapperId"
      v-bind="tooltip"
    />
  </button>
</template>

<script>
import SAMORA_SIZES from '../../../utils/constants/samora-sizes.enum';
import SAMORA_STYLES from '../../../utils/constants/samora-styles.enum';
import SAMORA_TYPES from '../../../utils/constants/samora-types.enum';
import SamIcon, { ICON_SIZES } from '../SamIcon/SamIcon.vue';
import SamTooltip from '../SamTooltip/SamTooltip.vue';
import { uuidv4 } from '../../../utils/utils';

const { BUTTON: SAMORA_BUTTON_TYPES } = SAMORA_TYPES;

export const BUTTON_TYPES = SAMORA_BUTTON_TYPES;

export const BUTTON_SIZES = {
  X_SMALL: SAMORA_SIZES.X_SMALL,
  SMALL: SAMORA_SIZES.SMALL,
  MEDIUM: SAMORA_SIZES.MEDIUM,
  LARGE: SAMORA_SIZES.LARGE,
};

export const iconSizesToPxMapper = new Map([
  [ICON_SIZES.X_SMALL, 12],
  [ICON_SIZES.SMALL, 14],
  [ICON_SIZES.MEDIUM, 16],
  [ICON_SIZES.LARGE, 20],
]);

export const BUTTON_STYLES = {
  PRIMARY: SAMORA_STYLES.PRIMARY,
  SECONDARY: SAMORA_STYLES.SECONDARY,
  DANGER: SAMORA_STYLES.DANGER,
  SUCCESS: SAMORA_STYLES.SUCCESS,
  WARNING: SAMORA_STYLES.WARNING,
  INHERITED: SAMORA_STYLES.INHERITED,
  LIGHT: SAMORA_STYLES.LIGHT,
};

export default {
  name: 'SamButton',
  components: { SamIcon, SamTooltip },
  props: {
    /**
     * The HTML id attribute. We need to define it here
     * because we access this.id in the computed property
     */
    id: {
      type: String,
      required: false,
      default: undefined,
    },
    /**
     * The Button type (html attribute type)
     */
    type: {
      type: String,
      required: false,
      default: 'button',
    },
    /**
     * The Button title (html title prop)
     */
    title: {
      type: String,
      required: false,
      default: undefined,
    },
    /**
     * The button label
     */
    label: {
      type: String,
      required: false,
      default: '',
    },
    /**
     * The Samora styles to be applied
     * @values primary, warning, success, danger
     */
    samStyle: {
      type: String,
      required: false,
      default: 'primary',
      validator: (val) => Object.values(BUTTON_STYLES).includes(val),
    },
    /**
     * The Samora button type to be applied
     * @values default, outlined, text
     */
    samType: {
      type: String,
      required: false,
      default: 'default',
      validator: (val) => Object.values(BUTTON_TYPES).includes(val),
    },
    /**
     * The button size. It can be any of the following: xs, sm, md, lg.
     * @values xs, sm, md, lg
     */
    size: {
      type: [String],
      required: false,
      default: SAMORA_SIZES.MEDIUM,
      validator: (val) => Array.from(iconSizesToPxMapper.keys()).includes(val),
    },
    /**
     * If the Button is disabled
     * @values true, false
     */
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * Button SamIcon name. Depends on the SamIcon component, where new icons should be added as SVGs
     */
    icon: {
      type: String,
      required: false,
      default: undefined,
    },
    iconConfig: {
      /** @type { IconConfiguration } */
      type: Object,
      required: false,
      default: () => ({}),
    },
    /**
     * Button tooltip bind attrs.
     * If set a tooltip will be displayed with the attrs set in `tooltip`
     */
    tooltip: {
      type: Object,
      required: false,
      default: undefined,
    },
    /**
     * Button active state.
     */
    active: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      uuid: uuidv4(),
    };
  },
  computed: {
    buttonWrapperId() {
      return this.id || `button__${this.uuid}`;
    },
    buttonClassObject() {
      return [
        `sam-button is-${this.samStyle} is-${this.samType}`,
        { 'has-icon': this.icon && this.label },
        { 'is-icon': this.icon && !this.label },
        { 'is-active': this.active },
      ];
    },
    cssStyle() {
      const buttonSize = iconSizesToPxMapper.get(this.size);
      return {
        '--button-size': `${buttonSize}px`,
      };
    },
    iconSize() {
      if (this.iconConfig?.size) return this.iconConfig.size;

      return this.label ? ICON_SIZES.X_SMALL : ICON_SIZES.SMALL;
    },
    noLabel() {
      return !this.label || this.label?.length === 0;
    },
    iconProps() {
      return {
        icon: this.icon,
        size: this.size,
        ...this.iconConfig,
      };
    },
  },
};
</script>

<style scoped lang="scss">
button.sam-button {
  @extend .px-4, .py-1-half, .text--button, .d-inline-flex, .items-center, .center-xs;
  font-size: var(--button-size); // from computed property cssStyle
  position: relative;
  border-radius: $size--1;
  cursor: pointer;
  text-align: center;
  border: none;
  transition: background-color $default-time $default-easing,
    outline-color $default-time $default-easing, color $default-time $default-easing;

  &:disabled {
    pointer-events: none;
  }

  // Hack so we don't use border property on button as it extends the size of button.
  &:not(.is-text):focus::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
    outline: 3px solid $informative--border;
    border-radius: $size--1;
  }

  &.is-default {
    &.is-primary {
      background-color: $informative--main;
      color: $pure--white;

      &:hover {
        background-color: $informative--hover;
      }

      &:active {
        background-color: $informative--pressed;
      }
    }

    &.is-danger {
      background-color: $error--main;
      color: $pure--white;

      &:hover {
        background-color: $error--hover;
      }

      &:active {
        background-color: $error--pressed;
      }
    }

    &.is-warning {
      background-color: $warning--main;
      color: $pure--white;

      &:hover {
        background-color: $warning--hover;
      }

      &:active {
        background-color: $warning--pressed;
      }
    }

    &.is-success {
      background-color: $success--main;
      color: $pure--white;

      &:hover {
        background-color: $success--hover;
      }

      &:active {
        background-color: $success--pressed;
      }
    }

    &.is-secondary {
      background-color: $info-secondary--main;
      color: $pure--white;

      &:hover {
        background-color: $info-secondary--hover;
      }

      &:active {
        background-color: $info-secondary--pressed;
      }
    }

    &.is-light {
      background-color: $pure--white;
      color: $primary--black-rock;

      &:hover {
        color: $accent--unbabel-blue;
      }

      &:active {
        color: $informative--pressed;
      }
    }

    &:disabled {
      background-color: $neutral--30;
      color: $neutral--60;
    }
  }

  &.is-outlined {
    background-color: transparent;
    outline: $size--0--fourth solid $informative--main;
    outline-offset: -$size--0--fourth;

    &.is-primary {
      color: $informative--main;
      outline-color: $informative--main;

      &:hover {
        outline-color: $informative--hover;
        color: $informative--hover;
      }

      &:active {
        color: $informative--pressed;
        outline-color: $informative--hover;
      }

      &.is-active {
        background-color: $informative--surface;
      }
    }

    &.is-secondary {
      color: $info-secondary--main;
      outline-color: $info-secondary--main;

      &:hover {
        outline-color: $info-secondary--hover;
        color: $info-secondary--hover;
      }

      &:active {
        color: $info-secondary--pressed;
        outline-color: $info-secondary--hover;
      }

      &.is-active {
        background-color: $info-secondary--surface;
      }
    }

    &.is-danger {
      color: $error--main;
      outline-color: $error--main;

      &:hover {
        outline-color: $error--hover;
        color: $error--hover;
      }

      &:active {
        color: $error--pressed;
        outline-color: $error--pressed;
      }

      &.is-active {
        background-color: $error--surface;
      }
    }

    &.is-warning {
      color: $warning--main;
      outline-color: $warning--main;

      &:hover {
        outline-color: $warning--hover;
        color: $warning--hover;
      }

      &:active {
        color: $warning--pressed;
        outline-color: $warning--pressed;
      }

      &.is-active {
        background-color: $warning--surface;
      }
    }

    &.is-success {
      color: $success--main;
      outline-color: $success--main;

      &:hover {
        outline-color: $success--hover;
        color: $success--hover;
      }

      &:active {
        color: $success--pressed;
        outline-color: $success--pressed;
      }

      &.is-active {
        background-color: $success--surface;
      }
    }

    &.is-inherited {
      color: inherit;
      outline-color: inherit;

      &:hover {
        outline-color: inherit;
        color: inherit;
      }

      &:active {
        color: inherit;
        outline-color: inherit;
      }
    }

    &.is-light {
      color: $pure--white;
      outline-color: $pure--white;
    }

    &:disabled {
      outline-color: $neutral--30;
      color: $neutral--60;
    }
  }

  &.is-text {
    background-color: transparent;

    &.is-primary {
      color: $informative--main;

      &:hover {
        color: $informative--hover;
      }

      &:active {
        color: $informative--pressed;
      }
    }

    &.is-secondary {
      color: $info-secondary--main;

      &:hover {
        color: $info-secondary--hover;
      }

      &:active {
        color: $info-secondary--pressed;
      }
    }

    &.is-danger {
      color: $error--main;

      &:hover {
        color: $error--hover;
      }

      &:active {
        color: $error--pressed;
      }
    }

    &.is-warning {
      color: $warning--main;

      &:hover {
        color: $warning--hover;
      }

      &:active {
        color: $warning--pressed;
      }
    }

    &.is-success {
      color: $success--main;

      &:hover {
        color: $success--hover;
      }

      &:active {
        color: $success--pressed;
      }
    }

    &.is-inherited {
      color: inherit;

      &:hover {
        color: inherit;
      }

      &:active {
        color: inherit;
      }
    }

    &.is-light {
      color: $pure--white;
    }

    &:disabled {
      color: $neutral--60;
    }
  }

  &.has-icon {
    @extend .pr-3;
  }

  &.is-icon {
    @extend .p-2;

    &:disabled {
      color: $neutral--50;
    }
  }

  .sam-button__content {
    @extend .d-flex, .center-xs;
  }

  .sam-button__icon {
    @extend .d-flex, .middle-xs;

    &:not(.icon-only) {
      @extend .ml-2;
    }

    :deep(path) {
      fill: currentColor;
    }
  }

  .sam-button__label {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}
</style>
